import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { LayerEditor } from "components/common/plan/layerEditor/LayerEditor";
import { MixLayerState } from "components/common/plan/layerEditor/types";
import { WarnedSteelGradesCell } from "components/common/plan/WarnedSteelGradesCell";
import {
  ImpuritiesPlot,
  ImpuritiesPlotSkeleton,
} from "components/common/plots/ImpuritiesPlotByTenant";
import { useTenantTranslation } from "hooks/formatters";
import { useMixGrouping } from "hooks/useMixGrouping";
import React, { useCallback } from "react";
import {
  ChildMixUpdate,
  MixMaterial,
  SingleMixSummary,
  SteelGrade,
  useSummarisePlanResultsMutation,
} from "src/store/api/generatedApi";
import {
  planMixTableEditsSlice,
  selectPlanMixTableEditEditedRows,
} from "src/store/slices/planMixesTableEditSlice";
import { useAppDispatch, useAppSelector, useAppStore } from "src/store/store";
import { typeSafeObjectFromEntries } from "src/utils/typeSafeObjectFromEntries";
import { PopoverMixTable } from "./PopoverMixTable";
import { Material, Row } from "./types";
import { ImpuritiesConstraint } from "components/common/plots/ImpuritiesPlotCommon";
import { GridColumnGroup } from "@mui/x-data-grid-premium";
import { PlanId } from "../boundary/PlanId";
import { Period } from "hooks/periodIndex";
import { PopoverLayerEditor } from "./columns";

interface Props {
  planId: PlanId;
  period: Period;
  steelGrades: SteelGrade[];
  selectedSteelGradeIds: number[];
  materials: Material[];
  mixId: number;
  hasDri: boolean;
  columnGroupingModel: GridColumnGroup[];
  disableEdit: boolean;
  description?: string;
  setDescription?: (description: string) => void;
  preloadedSummary?: SingleMixSummary | null;
  preloadedDri?: MixMaterial | null;
  layerEditor?: PopoverLayerEditor;
}

const useSummarisedPlanMix = ({
  planId,
  period,
  mixId,
  plottedSteelGradeId,
  preloadedSummary,
}: {
  planId: PlanId;
  period: Period;
  mixId: number;
  plottedSteelGradeId: number | null;
  preloadedSummary?: SingleMixSummary | null;
}) => {
  const [, summarisedPlanResults] = useSummarisePlanResultsMutation({
    fixedCacheKey: `${planId}-${period}`,
  });

  const summary =
    preloadedSummary !== undefined
      ? preloadedSummary
      : summarisedPlanResults.data?.mixes.find(
          (result) => result.original_mix_id === mixId
        )?.mix.summary;

  const constraints: ImpuritiesConstraint[] | undefined = React.useMemo(() => {
    return summary?.chemistry
      .map((chemistry) => {
        const steelGradeRisk = plottedSteelGradeId
          ? chemistry.steel_grade_risks.find(
              (steelGradeRisk) =>
                steelGradeRisk.steel_grade_id === plottedSteelGradeId
            )
          : null;
        if (steelGradeRisk) {
          const quantiles = Object.fromEntries(
            chemistry.quantiles.map((q) => [q.quantile, q.weight_percent])
          );
          return {
            name: chemistry.constraint_name,
            quantiles,
            weightPercentMinTolerance:
              steelGradeRisk.weight_percent_min_tolerance,
            weightPercentMaxTolerance:
              steelGradeRisk.weight_percent_max_tolerance,
            expectedOverToleranceRatePercent:
              steelGradeRisk.expected_over_tolerance_rate_percent,
            expectedUnderToleranceRatePercent:
              steelGradeRisk.expected_under_tolerance_rate_percent,
            expectedFailureRatePercent:
              steelGradeRisk.expected_over_tolerance_rate_percent +
              steelGradeRisk.expected_under_tolerance_rate_percent,
          };
        } else {
          return null;
        }
      })
      .filter(
        (
          chemistry: ImpuritiesConstraint | null
        ): chemistry is ImpuritiesConstraint => chemistry !== null
      );
  }, [summary, plottedSteelGradeId]);

  return {
    constraints,
    isLoading:
      preloadedSummary !== undefined ? false : summarisedPlanResults.isLoading,
    isError:
      preloadedSummary !== undefined ? false : summarisedPlanResults.isError,
  };
};

const PLOT_HEIGHT = 330;

interface MixLayerEditorPanelProps {
  mixId: number;
  layerEditor: PopoverLayerEditor;
}

const MixLayerEditorPanel = ({
  mixId,
  layerEditor,
}: MixLayerEditorPanelProps) => {
  const mixLayerState = layerEditor.getLayerStateForMix(mixId);

  // layerPreviewData (driven by the latest summarise response) is the source
  // of truth for the embedded LayerEditor — for non-manual mixes it carries
  // the backend's re-stratified baskets, for manual mixes it carries the
  // user's exact layers.
  const initialState = React.useMemo<MixLayerState[]>(
    () => (mixLayerState ? [mixLayerState] : []),
    [mixLayerState]
  );

  const handleStateChange = useCallback(
    (mixUpdates: ChildMixUpdate[], editedMixIds: ReadonlySet<number>) => {
      layerEditor.onMixLayerStateChange(
        mixId,
        mixUpdates,
        editedMixIds.has(mixId)
      );
    },
    [layerEditor, mixId]
  );

  const manuallyLayeredMixIds = React.useMemo(
    () =>
      layerEditor.isMixManuallyLayered(mixId)
        ? new Set<number>([mixId])
        : new Set<number>(),
    [layerEditor, mixId]
  );

  if (initialState.length === 0) {
    return null;
  }

  return (
    <LayerEditor
      initialState={initialState}
      materials={layerEditor.materials}
      onStateChange={handleStateChange}
      weightsEditable
      massScaleFactor={layerEditor.baseMassScaleFactor}
      size="comfortable"
      manuallyLayeredMixIds={manuallyLayeredMixIds}
      onResetMixLayers={layerEditor.onResetMixLayers}
    />
  );
};

export const PopoverSteelGradesCell = ({
  planId,
  period,
  steelGrades,
  selectedSteelGradeIds,
  materials,
  mixId,
  hasDri,
  columnGroupingModel,
  disableEdit,
  description,
  setDescription,
  preloadedSummary,
  preloadedDri,
  layerEditor,
}: Props) => {
  const [plottedSteelGradeId, setPlottedSteelGradeId] = React.useState<
    number | null
  >(null);
  const { t } = useTenantTranslation();
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const editedRows = useAppSelector((state) =>
    selectPlanMixTableEditEditedRows(state, planId, period)
  );
  const handleOnChange = React.useCallback(
    (rows: Row[]) => {
      const editedRows = selectPlanMixTableEditEditedRows(
        store.getState(),
        planId,
        period
      );
      const rowsLookup = typeSafeObjectFromEntries(
        rows.map((row) => [row.id, row])
      );
      const newRows = editedRows.map((row) => rowsLookup[row.id] ?? row);
      dispatch(
        planMixTableEditsSlice.actions.update([planId, period, newRows])
      );
    },
    [planId, period, dispatch, store]
  );

  const mixGrouping = useMixGrouping();
  const { constraints, isLoading, isError } = useSummarisedPlanMix({
    planId,
    period,
    mixId,
    plottedSteelGradeId,
    preloadedSummary,
  });

  const handleClose = useCallback(() => {
    setPlottedSteelGradeId(null);
  }, []);

  const isSummaryPreloaded = preloadedSummary !== undefined;

  // Stable references for the DataGrid props: the table sits inside the
  // dialog while other state above (summarise mutation, layer-editor edits)
  // churns, so any inline-allocated prop hands DataGridPremium a fresh ref
  // each render. Combined with the 100ms commit delay, that races the
  // controlled-rows resync against the user's in-progress cell edit.
  const popoverRows = React.useMemo(
    () => editedRows.filter((row) => row.mixId === mixId),
    [editedRows, mixId]
  );
  const popoverColumnGroupingModel = React.useMemo(
    () => (isSummaryPreloaded ? [] : columnGroupingModel),
    [isSummaryPreloaded, columnGroupingModel]
  );
  const popoverPreloadedSummaries = React.useMemo(
    () => (isSummaryPreloaded ? { [mixId]: preloadedSummary } : undefined),
    [isSummaryPreloaded, mixId, preloadedSummary]
  );
  const popoverPreloadedDris = React.useMemo(
    () => (preloadedDri !== undefined ? { [mixId]: preloadedDri } : undefined),
    [mixId, preloadedDri]
  );

  return (
    <>
      <WarnedSteelGradesCell
        onClick={setPlottedSteelGradeId}
        focusedSteelGradeId={plottedSteelGradeId}
        planId={planId}
        period={period}
        mixId={mixId}
        steelGrades={steelGrades}
        selectedSteelGradeIds={selectedSteelGradeIds}
        description={description}
        setDescription={setDescription}
        preloadedSummary={preloadedSummary}
      />
      <Dialog
        maxWidth="xl"
        sx={{
          "& .MuiDialog-paper": {
            // Fix to always be "full height" (i.e. 90vh)
            height: "90vh",
          },
        }}
        open={plottedSteelGradeId !== null}
        onClose={handleClose}
      >
        <DialogTitle>
          {t("mix")}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={(theme) => ({
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        {plottedSteelGradeId !== null ? (
          <DialogContent>
            <Box sx={{ height: 350, mb: 2 }}>
              <PopoverMixTable
                disableEdit={disableEdit}
                materials={materials}
                focusedSteelGradeId={plottedSteelGradeId}
                selectedSteelGradeIds={selectedSteelGradeIds}
                planId={planId}
                period={period}
                mixGrouping={mixGrouping}
                hasDri={hasDri}
                rows={popoverRows}
                onChange={handleOnChange}
                onSteelGradeClick={setPlottedSteelGradeId}
                columnGroupingModel={popoverColumnGroupingModel}
                preloadedSummaries={popoverPreloadedSummaries}
                preloadedDris={popoverPreloadedDris}
              />
            </Box>
            {isLoading ? (
              <ImpuritiesPlotSkeleton height={PLOT_HEIGHT} />
            ) : isError ? (
              <Alert severity="error">{t("errorLoadingPlot")}</Alert>
            ) : constraints && constraints.length > 0 ? (
              <ImpuritiesPlot constraints={constraints} height={PLOT_HEIGHT} />
            ) : null}
            {layerEditor ? (
              <Box sx={{ mt: 3 }}>
                <MixLayerEditorPanel mixId={mixId} layerEditor={layerEditor} />
              </Box>
            ) : null}
          </DialogContent>
        ) : null}
      </Dialog>
    </>
  );
};
